/**
 * post-V9 Section 0 Iter 3 — aggregate compact store.
 *
 * Combines per-type compact stores for all 18 primitive types (15 base
 * + RevisionCloud / Wipeout / AttDef added in Phase 3) behind a single
 * CompactStore that consumes a `DrawingEntity[]` mixed-array (plus
 * AttDef from block-children paths) and dispatches to the appropriate
 * per-type append.
 *
 * **LINE handling:** the existing Iter 2 CompactLineStore
 * (compactEntityStore.ts) is wrapped here for completeness — it uses
 * its OWN string table, not the shared one, because Iter 2 is frozen
 * for measurement comparisons. Iter 4 will migrate LINE to the shared
 * table.
 *
 * **AttDef populate limitation (Phase 3):** `populateCompactCache` in
 * `mutationStore.ts` walks `space.entities` per space — it does not
 * visit `block.entities[]`. The AttDef per-type store + dispatch are
 * fully wired so any future block-children populate path can feed
 * AttDef through `appendEntity` (or `appendToCompactCache`) without
 * additional changes. Reducers that call `appendToCompactCache` with
 * an AttDef entity will land in `store.attdefs` correctly.
 */
import type {
  ArcEntity,
  ArrayRectEntity,
  AttdefEntity,
  BlockInsertEntity,
  CircleEntity,
  DimensionEntity,
  DrawingEntity,
  EllipseEntity,
  HatchEntity,
  LineEntity,
  LwpolylineEntity,
  OleFrameEntity,
  PolylineEntity,
  TableEntity,
  MtextEntity,
  MultileaderEntity,
  PointEntity,
  RevisionCloudEntity,
  SplineEntity,
  TextEntity,
  ViewportEntity,
  WipeoutEntity,
} from "@dxf-ai-editor/drawing-model";

import {
  appendLine,
  createCompactLineStore,
  estimateCompactBytesPerLine,
  lineFromCompact,
  type CompactLineStore,
} from "../compactEntityStore";
import {
  appendDimension,
  appendMultileader,
  createCompactDimensionStore,
  createCompactMultileaderStore,
  dimensionFromCompact,
  estimateDimensionBytesPerEntity,
  estimateMultileaderBytesPerEntity,
  multileaderFromCompact,
  type CompactDimensionStore,
  type CompactMultileaderStore,
} from "./compactAnnotationStores";
import {
  appendArrayRect,
  appendBlockInsert,
  appendHatch,
  appendViewport,
  arrayRectFromCompact,
  blockInsertFromCompact,
  createCompactArrayRectStore,
  createCompactBlockInsertStore,
  createCompactHatchStore,
  createCompactViewportStore,
  estimateArrayRectBytesPerEntity,
  estimateBlockInsertBytesPerEntity,
  estimateHatchBytesPerEntity,
  estimateViewportBytesPerEntity,
  hatchFromCompact,
  viewportFromCompact,
  type CompactArrayRectStore,
  type CompactBlockInsertStore,
  type CompactHatchStore,
  type CompactViewportStore,
} from "./compactComplexStores";
import {
  appendLwpolyline,
  appendSpline,
  createCompactLwpolylineStore,
  createCompactSplineStore,
  estimateLwpolylineBytesPerEntity,
  estimateSplineBytesPerEntity,
  lwpolylineFromCompact,
  splineFromCompact,
  type CompactLwpolylineStore,
  type CompactSplineStore,
} from "./compactPolylineStores";
import {
  appendArc,
  appendCircle,
  appendEllipse,
  appendPoint,
  arcFromCompact,
  circleFromCompact,
  createCompactArcStore,
  createCompactCircleStore,
  createCompactEllipseStore,
  createCompactPointStore,
  ellipseFromCompact,
  estimateArcBytesPerEntity,
  estimateCircleBytesPerEntity,
  estimateEllipseBytesPerEntity,
  estimatePointBytesPerEntity,
  pointFromCompact,
  type CompactArcStore,
  type CompactCircleStore,
  type CompactEllipseStore,
  type CompactPointStore,
} from "./compactSimpleGeometry";
import {
  appendMtext,
  appendText,
  createCompactMtextStore,
  createCompactTextStore,
  estimateMtextBytesPerEntity,
  estimateTextBytesPerEntity,
  mtextFromCompact,
  textFromCompact,
  type CompactMtextStore,
  type CompactTextStore,
} from "./compactTextStores";
import {
  appendAttdef,
  appendRevisionCloud,
  appendWipeout,
  attdefFromCompact,
  createCompactAttdefStore,
  createCompactRevisionCloudStore,
  createCompactWipeoutStore,
  estimateAttdefBytesPerEntity,
  estimateRevisionCloudBytesPerEntity,
  estimateWipeoutBytesPerEntity,
  revisionCloudFromCompact,
  wipeoutFromCompact,
  type CompactAttdefStore,
  type CompactRevisionCloudStore,
  type CompactWipeoutStore,
} from "./compactCoverageStores";
import {
  createSharedStringTable,
  size as stringTableSize,
  totalCharCount,
  type SharedStringTable,
} from "./sharedStringTable";
import { baseMetadataBytes, type BaseColumns } from "./compactStoreBase";
import {
  appendHeavyPolyline,
  createCompactHeavyPolylineStore,
  estimateHeavyPolylineBytesPerEntity,
  heavyPolylineFromCompact,
  type CompactHeavyPolylineStore,
} from "./compactHeavyPolylineStore";
import {
  appendOleFrame,
  appendTable,
  createCompactOleFrameStore,
  createCompactTableStore,
  estimateOleFrameBytesPerEntity,
  estimateTableBytesPerEntity,
  oleFrameFromCompact,
  tableFromCompact,
  type CompactOleFrameStore,
  type CompactTableStore,
} from "./compactTableOleStores";

export interface TypeCounts {
  Line: number;
  Point: number;
  Circle: number;
  Arc: number;
  Ellipse: number;
  Spline: number;
  Lwpolyline: number;
  Text: number;
  MText: number;
  Dimension: number;
  Multileader: number;
  Hatch: number;
  Block: number;
  ArrayRect: number;
  Viewport: number;
  RevisionCloud: number;
  Wipeout: number;
  Attdef: number;
  Polyline: number;
  Table: number;
  OleFrame: number;
  Other: number;
}

export interface CompactStore {
  readonly stringTable: SharedStringTable;
  readonly lines: CompactLineStore;
  readonly points: CompactPointStore;
  readonly circles: CompactCircleStore;
  readonly arcs: CompactArcStore;
  readonly ellipses: CompactEllipseStore;
  readonly splines: CompactSplineStore;
  readonly lwpolylines: CompactLwpolylineStore;
  readonly texts: CompactTextStore;
  readonly mtexts: CompactMtextStore;
  readonly dimensions: CompactDimensionStore;
  readonly multileaders: CompactMultileaderStore;
  readonly hatches: CompactHatchStore;
  readonly blockInserts: CompactBlockInsertStore;
  readonly arrayRects: CompactArrayRectStore;
  readonly viewports: CompactViewportStore;
  readonly revisionClouds: CompactRevisionCloudStore;
  readonly wipeouts: CompactWipeoutStore;
  /**
   * Phase 3 — ATTDEF per-type store.
   *
   * Known limitation: AttDef appears only in `block.entities[]` (block
   * definitions), never in model/paper space directly. The current
   * `populateCompactCache` flow walks `space.entities` per space and
   * therefore does not feed ATTDEF entries into this store. The store
   * + dispatch are wired so any future block-children populate path
   * (or per-block compact cache) can route through `appendEntity`
   * without further changes. Reducers that call `appendToCompactCache`
   * with an ATTDEF entity will land here.
   */
  readonly attdefs: CompactAttdefStore;
  /**
   * Legacy heavy `POLYLINE`, `ACAD_TABLE` and `OLE2FRAME`. Present for the
   * SAME reason every store above is: on the streaming import path
   * (`discardEntitiesAfterStreaming`) the read-through document is rebuilt
   * from this store, so a supported entity type with no store here is
   * dropped in the browser even though the non-streaming bridge imports it.
   */
  readonly polylines: CompactHeavyPolylineStore;
  readonly tables: CompactTableStore;
  readonly oleFrames: CompactOleFrameStore;
  counts: TypeCounts;
}

export function createCompactStore(): CompactStore {
  return {
    stringTable: createSharedStringTable(),
    lines: createCompactLineStore(),
    points: createCompactPointStore(),
    circles: createCompactCircleStore(),
    arcs: createCompactArcStore(),
    ellipses: createCompactEllipseStore(),
    splines: createCompactSplineStore(),
    lwpolylines: createCompactLwpolylineStore(),
    texts: createCompactTextStore(),
    mtexts: createCompactMtextStore(),
    dimensions: createCompactDimensionStore(),
    multileaders: createCompactMultileaderStore(),
    hatches: createCompactHatchStore(),
    blockInserts: createCompactBlockInsertStore(),
    arrayRects: createCompactArrayRectStore(),
    viewports: createCompactViewportStore(),
    revisionClouds: createCompactRevisionCloudStore(),
    wipeouts: createCompactWipeoutStore(),
    attdefs: createCompactAttdefStore(),
    polylines: createCompactHeavyPolylineStore(),
    tables: createCompactTableStore(),
    oleFrames: createCompactOleFrameStore(),
    counts: {
      Line: 0,
      Point: 0,
      Circle: 0,
      Arc: 0,
      Ellipse: 0,
      Spline: 0,
      Lwpolyline: 0,
      Text: 0,
      MText: 0,
      Dimension: 0,
      Multileader: 0,
      Hatch: 0,
      Block: 0,
      ArrayRect: 0,
      Viewport: 0,
      RevisionCloud: 0,
      Wipeout: 0,
      Attdef: 0,
      Polyline: 0,
      Table: 0,
      OleFrame: 0,
      Other: 0,
    },
  };
}

/**
 * post-V9 Section 0 Iter 3 — append a single entity into its matching
 * per-type store.
 *
 * Phase 3 — accepts `DrawingEntity | AttdefEntity`. AttDef is only legal
 * inside block-definition `entities[]` (not model/paper space) so the
 * `DrawingEntity` union doesn't include it. The compact-store dispatch
 * supports the wider type so a future block-children populate path can
 * funnel ATTDEFs through the same `appendEntity` without bespoke wiring.
 */
export function appendEntity(
  store: CompactStore,
  entity: DrawingEntity | AttdefEntity,
): number {
  const t = store.stringTable;
  switch (entity.type) {
    case "Line":
      store.counts.Line++;
      return appendLine(store.lines, entity as LineEntity);
    case "Point":
      store.counts.Point++;
      return appendPoint(store.points, t, entity as PointEntity);
    case "Circle":
      store.counts.Circle++;
      return appendCircle(store.circles, t, entity as CircleEntity);
    case "Arc":
      store.counts.Arc++;
      return appendArc(store.arcs, t, entity as ArcEntity);
    case "Ellipse":
      store.counts.Ellipse++;
      return appendEllipse(store.ellipses, t, entity as EllipseEntity);
    case "Spline":
      store.counts.Spline++;
      return appendSpline(store.splines, t, entity as SplineEntity);
    case "LWPOLYLINE":
      store.counts.Lwpolyline++;
      return appendLwpolyline(store.lwpolylines, t, entity as LwpolylineEntity);
    case "Text":
      store.counts.Text++;
      return appendText(store.texts, t, entity as TextEntity);
    case "MText":
      store.counts.MText++;
      return appendMtext(store.mtexts, t, entity as MtextEntity);
    case "Dimension":
      store.counts.Dimension++;
      return appendDimension(store.dimensions, t, entity as DimensionEntity);
    case "Multileader":
      store.counts.Multileader++;
      return appendMultileader(
        store.multileaders,
        t,
        entity as MultileaderEntity,
      );
    case "Hatch":
      store.counts.Hatch++;
      return appendHatch(store.hatches, t, entity as HatchEntity);
    case "Block":
      store.counts.Block++;
      return appendBlockInsert(
        store.blockInserts,
        t,
        entity as BlockInsertEntity,
      );
    case "Rectangular Associative Array":
      store.counts.ArrayRect++;
      return appendArrayRect(store.arrayRects, t, entity as ArrayRectEntity);
    case "Viewport":
      store.counts.Viewport++;
      return appendViewport(store.viewports, t, entity as ViewportEntity);
    case "Revision Cloud":
      store.counts.RevisionCloud++;
      return appendRevisionCloud(
        store.revisionClouds,
        t,
        entity as RevisionCloudEntity,
      );
    case "Wipeout":
      store.counts.Wipeout++;
      return appendWipeout(store.wipeouts, t, entity as WipeoutEntity);
    case "Attdef":
      store.counts.Attdef++;
      return appendAttdef(store.attdefs, t, entity as AttdefEntity);
    case "Polyline":
      store.counts.Polyline++;
      return appendHeavyPolyline(store.polylines, t, entity as PolylineEntity);
    case "Table":
      store.counts.Table++;
      return appendTable(store.tables, t, entity as TableEntity);
    case "OLE Frame":
      store.counts.OleFrame++;
      return appendOleFrame(store.oleFrames, t, entity as OleFrameEntity);
    default:
      store.counts.Other++;
      return -1;
  }
}

/**
 * post-V9 Section 0 Iter 5 — per-type-store kind discriminator used by the
 * id→location index in `mutationStore.ts`. Mirrors the shape of every per-
 * type store inside `CompactStore` so consumers can dispatch reconstruction
 * без inspecting `entity.type` again.
 */
export type CompactStoreKind =
  | "lines"
  | "points"
  | "circles"
  | "arcs"
  | "ellipses"
  | "splines"
  | "lwpolylines"
  | "texts"
  | "mtexts"
  | "dimensions"
  | "multileaders"
  | "hatches"
  | "blockInserts"
  | "arrayRects"
  | "viewports"
  | "revisionClouds"
  | "wipeouts"
  | "polylines"
  | "tables"
  | "oleFrames"
  | "attdefs";

/**
 * post-V9 Section 0 Iter 5 — generic dispatch lookup that reconstructs a
 * full `DrawingEntity` from the appropriate per-type compact store via
 * the matching `*FromCompact(store, table?, index)` reader.
 *
 * Used by the renderer's compact-priority lookup path
 * (`getEntityFromCompactCache` in `mutationStore.ts`) so the cache-direct
 * render pass can lift visible ids without touching the legacy
 * `__entitiesCache: Map<string, DrawingEntity[]>`.
 *
 * Returns `undefined` if `index` is out of range for the addressed store.
 * Throws synchronously for an unknown `kind` (programmer error — not a
 * recoverable runtime condition).
 *
 * **Note (LINE):** `lineFromCompact(store, index)` does not take a string
 * table — Iter 2 froze the per-store string table for measurement
 * comparisons. The other 13 readers accept the shared `store.stringTable`.
 */
export function entityFromCompactStore(
  store: CompactStore,
  kind: CompactStoreKind,
  index: number,
): DrawingEntity | AttdefEntity | undefined {
  const t = store.stringTable;
  switch (kind) {
    case "lines":
      if (index < 0 || index >= store.lines.count) return undefined;
      if (store.lines.tombstones[index] === 1) return undefined;
      return lineFromCompact(store.lines, index);
    case "points":
      if (index < 0 || index >= store.points.count) return undefined;
      if (store.points.base.tombstones[index] === 1) return undefined;
      return pointFromCompact(store.points, t, index);
    case "circles":
      if (index < 0 || index >= store.circles.count) return undefined;
      if (store.circles.base.tombstones[index] === 1) return undefined;
      return circleFromCompact(store.circles, t, index);
    case "arcs":
      if (index < 0 || index >= store.arcs.count) return undefined;
      if (store.arcs.base.tombstones[index] === 1) return undefined;
      return arcFromCompact(store.arcs, t, index);
    case "ellipses":
      if (index < 0 || index >= store.ellipses.count) return undefined;
      if (store.ellipses.base.tombstones[index] === 1) return undefined;
      return ellipseFromCompact(store.ellipses, t, index);
    case "splines":
      if (index < 0 || index >= store.splines.count) return undefined;
      if (store.splines.base.tombstones[index] === 1) return undefined;
      return splineFromCompact(store.splines, t, index);
    case "lwpolylines":
      if (index < 0 || index >= store.lwpolylines.count) return undefined;
      if (store.lwpolylines.base.tombstones[index] === 1) return undefined;
      return lwpolylineFromCompact(store.lwpolylines, t, index);
    case "texts":
      if (index < 0 || index >= store.texts.count) return undefined;
      if (store.texts.base.tombstones[index] === 1) return undefined;
      return textFromCompact(store.texts, t, index);
    case "mtexts":
      if (index < 0 || index >= store.mtexts.count) return undefined;
      if (store.mtexts.base.tombstones[index] === 1) return undefined;
      return mtextFromCompact(store.mtexts, t, index);
    case "dimensions":
      if (index < 0 || index >= store.dimensions.count) return undefined;
      if (store.dimensions.base.tombstones[index] === 1) return undefined;
      return dimensionFromCompact(store.dimensions, t, index);
    case "multileaders":
      if (index < 0 || index >= store.multileaders.count) return undefined;
      if (store.multileaders.base.tombstones[index] === 1) return undefined;
      return multileaderFromCompact(store.multileaders, t, index);
    case "hatches":
      if (index < 0 || index >= store.hatches.count) return undefined;
      if (store.hatches.base.tombstones[index] === 1) return undefined;
      return hatchFromCompact(store.hatches, t, index);
    case "blockInserts":
      if (index < 0 || index >= store.blockInserts.count) return undefined;
      if (store.blockInserts.base.tombstones[index] === 1) return undefined;
      return blockInsertFromCompact(store.blockInserts, t, index);
    case "arrayRects":
      if (index < 0 || index >= store.arrayRects.count) return undefined;
      if (store.arrayRects.base.tombstones[index] === 1) return undefined;
      return arrayRectFromCompact(store.arrayRects, t, index);
    case "viewports":
      if (index < 0 || index >= store.viewports.count) return undefined;
      if (store.viewports.base.tombstones[index] === 1) return undefined;
      return viewportFromCompact(store.viewports, t, index);
    case "revisionClouds":
      if (index < 0 || index >= store.revisionClouds.count) return undefined;
      if (store.revisionClouds.base.tombstones[index] === 1) return undefined;
      return revisionCloudFromCompact(store.revisionClouds, t, index);
    case "wipeouts":
      if (index < 0 || index >= store.wipeouts.count) return undefined;
      if (store.wipeouts.base.tombstones[index] === 1) return undefined;
      return wipeoutFromCompact(store.wipeouts, t, index);
    case "attdefs":
      if (index < 0 || index >= store.attdefs.count) return undefined;
      if (store.attdefs.base.tombstones[index] === 1) return undefined;
      return attdefFromCompact(store.attdefs, t, index);
    case "polylines":
      if (index < 0 || index >= store.polylines.count) return undefined;
      if (store.polylines.base.tombstones[index] === 1) return undefined;
      return heavyPolylineFromCompact(store.polylines, t, index);
    case "tables":
      if (index < 0 || index >= store.tables.count) return undefined;
      if (store.tables.base.tombstones[index] === 1) return undefined;
      return tableFromCompact(store.tables, t, index);
    case "oleFrames":
      if (index < 0 || index >= store.oleFrames.count) return undefined;
      if (store.oleFrames.base.tombstones[index] === 1) return undefined;
      return oleFrameFromCompact(store.oleFrames, t, index);
    default: {
      const exhaustive: never = kind;
      throw new Error(
        `entityFromCompactStore: unknown CompactStoreKind ${String(exhaustive)}`,
      );
    }
  }
}

export function compactStoreFromEntities(
  entities: ReadonlyArray<unknown>,
): CompactStore {
  const store = createCompactStore();
  for (const e of entities) {
    if (e == null || typeof e !== "object") continue;
    const type = (e as { type?: unknown }).type;
    if (typeof type !== "string") continue;
    appendEntity(store, e as DrawingEntity);
  }
  return store;
}

/**
 * Total ALLOCATED slots across every per-type store — live plus tombstoned.
 *
 * One definition rather than the two byte-identical sums `totalCount` and
 * `tombstoneFraction` used to carry: they answered the same question ("how
 * many slots does this store hold?") and had to be edited together every time
 * a per-type store was added, which is exactly the drift the duplication gate
 * exists to stop (adding the heavy-POLYLINE / ACAD_TABLE / OLE2FRAME stores
 * pushed the pair over its threshold).
 */
function rawSlotCount(store: CompactStore): number {
  return (
    store.lines.count +
    store.points.count +
    store.circles.count +
    store.arcs.count +
    store.ellipses.count +
    store.splines.count +
    store.lwpolylines.count +
    store.texts.count +
    store.mtexts.count +
    store.dimensions.count +
    store.multileaders.count +
    store.hatches.count +
    store.blockInserts.count +
    store.arrayRects.count +
    store.viewports.count +
    store.revisionClouds.count +
    store.wipeouts.count +
    store.attdefs.count +
    store.polylines.count +
    store.tables.count +
    store.oleFrames.count
  );
}

export function totalCount(store: CompactStore): number {
  // post-V9 Section 0 Iter 6.C — report live entity count, matching the
  // Map-era "how many entities exist in cache" semantic.
  const raw = rawSlotCount(store);
  const totalTombstones = totalTombstoneCount(store);
  return Math.max(0, raw - totalTombstones);
}

/**
 * post-V9 Section 0 Iter 6.C — sum of tombstoned slots across all per-type
 * stores. Used by `totalCount` (subtract from raw count) and
 * `tombstoneFraction` (numerator).
 */
function totalTombstoneCount(store: CompactStore): number {
  return (
    store.lines.tombstoneCount +
    store.points.base.tombstoneCount +
    store.circles.base.tombstoneCount +
    store.arcs.base.tombstoneCount +
    store.ellipses.base.tombstoneCount +
    store.splines.base.tombstoneCount +
    store.lwpolylines.base.tombstoneCount +
    store.texts.base.tombstoneCount +
    store.mtexts.base.tombstoneCount +
    store.dimensions.base.tombstoneCount +
    store.multileaders.base.tombstoneCount +
    store.hatches.base.tombstoneCount +
    store.blockInserts.base.tombstoneCount +
    store.arrayRects.base.tombstoneCount +
    store.viewports.base.tombstoneCount +
    store.revisionClouds.base.tombstoneCount +
    store.wipeouts.base.tombstoneCount +
    store.attdefs.base.tombstoneCount +
    store.polylines.base.tombstoneCount +
    store.tables.base.tombstoneCount +
    store.oleFrames.base.tombstoneCount
  );
}

/**
 * post-V9 Section 0 Iter 6.C — fraction of slots that are tombstoned across
 * the aggregate store. Returns 0 when the store is empty. Used by the
 * compaction trigger in `mutationStore.ts` (default threshold 0.25).
 */
export function tombstoneFraction(store: CompactStore): number {
  const raw = rawSlotCount(store);
  if (raw === 0) return 0;
  return totalTombstoneCount(store) / raw;
}

/**
 * post-V9 Section 0 Iter 6.C — rebuild the compact store with only live
 * (non-tombstoned) entries. Used when tombstone fraction crosses the
 * compaction threshold.
 *
 * Returns a fresh store + fresh id-index. Caller swaps the holder
 * references atomically. The old store + idIndex become GC-eligible
 * immediately if no consumer cached them across the swap.
 *
 * NOTE: re-runs `appendEntity` so per-type counts and shared string-table
 * intern IDs are recomputed from scratch — no stale interned indices
 * survive in the new table.
 */
export function compactCompactStore(
  oldStore: CompactStore,
  oldIdIndex: ReadonlyMap<string, { storeKind: CompactStoreKind; index: number }>,
): {
  store: CompactStore;
  idIndex: Map<string, { storeKind: CompactStoreKind; index: number }>;
} {
  const newStore = createCompactStore();
  const newIdIndex = new Map<
    string,
    { storeKind: CompactStoreKind; index: number }
  >();
  for (const [entityId, location] of oldIdIndex) {
    const entity = entityFromCompactStore(
      oldStore,
      location.storeKind,
      location.index,
    );
    // Defensive: tombstoned or out-of-range slots produce undefined and
    // are skipped. A consistent caller (mutationStore wiring in Commit 2)
    // will have already removed tombstoned ids from the id-index, so this
    // branch should be unreachable in practice.
    if (!entity) continue;
    const newIndex = appendEntity(newStore, entity);
    if (newIndex < 0) continue; // unknown type — defensive, "Other" bucket
    newIdIndex.set(entityId, { storeKind: location.storeKind, index: newIndex });
  }
  return { store: newStore, idIndex: newIdIndex };
}

export function estimateBytes(store: CompactStore): number {
  let total = 0;
  total += store.lines.count * estimateCompactBytesPerLine();
  total += store.points.count * estimatePointBytesPerEntity();
  total += store.circles.count * estimateCircleBytesPerEntity();
  total += store.arcs.count * estimateArcBytesPerEntity();
  total += store.ellipses.count * estimateEllipseBytesPerEntity();
  total += store.texts.count * estimateTextBytesPerEntity();
  total += store.mtexts.count * estimateMtextBytesPerEntity();
  total += store.dimensions.count * estimateDimensionBytesPerEntity();
  total += store.blockInserts.count * estimateBlockInsertBytesPerEntity();
  total += store.arrayRects.count * estimateArrayRectBytesPerEntity();
  total += store.viewports.count * estimateViewportBytesPerEntity();
  total += store.splines.count * estimateSplineBytesPerEntity(store.splines);
  total +=
    store.lwpolylines.count *
    estimateLwpolylineBytesPerEntity(store.lwpolylines);
  total +=
    store.multileaders.count *
    estimateMultileaderBytesPerEntity(store.multileaders);
  total += store.hatches.count * estimateHatchBytesPerEntity(store.hatches);
  total +=
    store.revisionClouds.count *
    estimateRevisionCloudBytesPerEntity(store.revisionClouds);
  total +=
    store.wipeouts.count * estimateWipeoutBytesPerEntity(store.wipeouts);
  total += store.attdefs.count * estimateAttdefBytesPerEntity();
  total +=
    store.polylines.count *
    estimateHeavyPolylineBytesPerEntity(store.polylines);
  total += store.tables.count * estimateTableBytesPerEntity(store.tables);
  total +=
    store.oleFrames.count * estimateOleFrameBytesPerEntity(store.oleFrames);
  const tableChars = totalCharCount(store.stringTable);
  total += tableChars * 2 + stringTableSize(store.stringTable) * 16;
  total += idBytes(store);
  total += metadataBytes(store);
  return total;
}

/**
 * Cost of the sparse common-metadata remainder (`BaseMetadata`) across every
 * per-type store. Counted here rather than inside the per-type
 * `estimate*BytesPerEntity` helpers because it is a per-ENTITY-THAT-HAS-ONE
 * cost, not a fixed per-slot one: a drawing whose entities carry no `330` /
 * ext-dict / 347 / 390 / 60 / 284 / 48 / unknown groups adds exactly zero.
 */
function metadataBytes(store: CompactStore): number {
  let n = baseMetadataBytes(store.lines.metadata);
  for (const base of baseColumnsOf(store)) n += baseMetadataBytes(base.metadata);
  return n;
}

function idBytes(store: CompactStore): number {
  let n = 0;
  for (let i = 0; i < store.lines.count; i++) {
    n += (store.lines.ids[i] ?? "").length * 2;
    n += (store.lines.spaceIds[i] ?? "").length * 2;
  }
  for (const s of baseColumnsOf(store)) {
    for (const id of s.ids) n += id.length * 2;
    for (const spaceId of s.spaceIds) n += spaceId.length * 2;
  }
  return n;
}

/**
 * Every per-type store's shared `BaseColumns`, in one place — LINE excluded
 * because it keeps its own frozen Iter-2 columns. One list, so a store added
 * to `CompactStore` cannot be accounted for by one walker and forgotten by
 * the other.
 */
function baseColumnsOf(store: CompactStore): readonly BaseColumns[] {
  return [
    store.points.base,
    store.circles.base,
    store.arcs.base,
    store.ellipses.base,
    store.splines.base,
    store.lwpolylines.base,
    store.texts.base,
    store.mtexts.base,
    store.dimensions.base,
    store.multileaders.base,
    store.hatches.base,
    store.blockInserts.base,
    store.arrayRects.base,
    store.viewports.base,
    store.revisionClouds.base,
    store.wipeouts.base,
    store.attdefs.base,
    store.polylines.base,
    store.tables.base,
    store.oleFrames.base,
  ];
}

export function averageBytesPerEntity(store: CompactStore): number {
  const count = totalCount(store);
  if (count === 0) return 0;
  return estimateBytes(store) / count;
}

/**
 * Phase 4 Wave 2 Task 4 — column-aware live walk.
 *
 * Iterates every non-tombstoned slot in the per-type store identified by
 * `kind`, optionally filtered by `spaceId`, and invokes `cb(entity, idx)`
 * for each live entry. The `idx` argument is the per-type-store slot
 * index (NOT a logical 0..count walk-index) so callers that want to
 * mutate through `setInCompactCache(entity.id, ...)` can also locate the
 * existing slot if they need to.
 *
 * **Why this exists (Wave 2 design note):**
 *  - After Wave 1's read-through wrapper, `space.entities.map(...)` in a
 *    reducer goes through `snapshotSpaceEntitiesFromHolder` —
 *    materializes a fresh `DrawingEntity[]` from the compact cache on
 *    every access. For hot bulk mutations (MOVE / COPY / ROTATE on
 *    1k+ entities) the per-entity object churn dominates.
 *  - `forEachLive` skips the intermediate array: the entity is built,
 *    handed to the callback, and the callback can decide to keep,
 *    transform, or discard it without the materialised array ever
 *    growing past one element.
 *  - The pattern below is the template for Wave 3 / follow-up reducer
 *    rewrites. Wave 2 lands the API + one proof point in
 *    `geometryReducer.ts` (`geometry/move-entities`).
 *
 * **Identity contract:** the `entity` argument is freshly constructed
 * per call — callers that need stable `===` identity must memoise on
 * `entity.id`.
 *
 * Pass `undefined` for `spaceId` to walk every live entry regardless of
 * containing space (useful for renderer-side spatial-index rebuilds).
 *
 * Example:
 * ```ts
 * forEachLive(store, "lines", "model", (entity, slotIndex) => {
 *   if (idsToMove.has(entity.id)) {
 *     const next = transformEntityCoords(entity, fn);
 *     setLineInPlace(store.lines, slotIndex, next);  // hypothetical
 *   }
 * });
 * ```
 */
export function forEachLive(
  store: CompactStore,
  kind: CompactStoreKind,
  spaceId: string | undefined,
  cb: (entity: DrawingEntity | AttdefEntity, index: number) => void,
): void {
  // Each per-type store exposes a `count` and either:
  //   - `lines` — top-level `tombstones` array + own `spaceIds` array;
  //   - others — `base.tombstones` + `base.spaceIds`.
  // We pivot the discriminator once and run the tight inner loop with
  // direct typed-array reads.
  if (kind === "lines") {
    const sub = store.lines;
    for (let i = 0; i < sub.count; i++) {
      if (sub.tombstones[i] === 1) continue;
      if (spaceId !== undefined && sub.spaceIds[i] !== spaceId) continue;
      const entity = entityFromCompactStore(store, "lines", i);
      if (entity === undefined) continue;
      cb(entity, i);
    }
    return;
  }

  // All non-line per-type stores share the BaseColumns shape.
  const sub = (() => {
    switch (kind) {
      case "points":
        return store.points;
      case "circles":
        return store.circles;
      case "arcs":
        return store.arcs;
      case "ellipses":
        return store.ellipses;
      case "splines":
        return store.splines;
      case "lwpolylines":
        return store.lwpolylines;
      case "texts":
        return store.texts;
      case "mtexts":
        return store.mtexts;
      case "dimensions":
        return store.dimensions;
      case "multileaders":
        return store.multileaders;
      case "hatches":
        return store.hatches;
      case "blockInserts":
        return store.blockInserts;
      case "arrayRects":
        return store.arrayRects;
      case "viewports":
        return store.viewports;
      case "revisionClouds":
        return store.revisionClouds;
      case "wipeouts":
        return store.wipeouts;
      case "attdefs":
        return store.attdefs;
      case "polylines":
        return store.polylines;
      case "tables":
        return store.tables;
      case "oleFrames":
        return store.oleFrames;
      default: {
        const _exhaustive: never = kind;
        void _exhaustive;
        return undefined;
      }
    }
  })();
  if (sub === undefined) return;
  const base = sub.base;
  for (let i = 0; i < sub.count; i++) {
    if (base.tombstones[i] === 1) continue;
    if (spaceId !== undefined && base.spaceIds[i] !== spaceId) continue;
    const entity = entityFromCompactStore(store, kind, i);
    if (entity === undefined) continue;
    cb(entity, i);
  }
}
